// Sugalarm — top-level app: theme bootstrap, frame variant choice, router.

import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { CgmAuthModal } from "./components/kiosk/CgmAuthModal";
import { CompactAlarmControl } from "./components/kiosk/CompactAlarmControl";
import { KioskFrame } from "./components/kiosk/KioskFrame";
import { PreviewOverlay } from "./components/kiosk/PreviewOverlay";
import { MissedReminderBanner } from "./components/reminders/MissedReminderBanner";
import { alarmStartedSounding, blurFocusedTextEntry } from "./lib/kioskKeyboard";
import {
  alarmSoundingForPhotos as alarmSoundingForPhotosFn,
  alarmStartedForCamera,
  sendAlarmStarted,
  setPhotoBlockedByAlarm,
} from "./lib/nativeCamera";
import { setAlarmSoundingForPicker } from "./lib/attributionPicker";
import { ReminderOverlay } from "./components/reminders/ReminderOverlay";
import { Logo } from "./components/ui/Logo";
import { WebGate } from "./screens/login/WebGate";
import { webAccessAllowed } from "./lib/webGate";
import { useActiveMember } from "./hooks/useActiveMember";
import { useAlarmClockMode, useClockMode } from "./hooks/useAlarmClockMode";
import { useAlarmEpisode } from "./hooks/useAlarmEpisode";
import { useAlarmPreview } from "./hooks/useAlarmPreview";
import { callQuietKey, useAlarmSounds } from "./hooks/useAlarmSounds";
import { useAppVersionPoller } from "./hooks/useAppVersionPoller";
import { useAuth } from "./hooks/useAuth";
import { useAutoAnnounce } from "./hooks/useAutoAnnounce";
import { useFrameVariant } from "./hooks/useFrameVariant";
import type { FrameVariant } from "./hooks/useFrameVariant";
import type { DisplayTheme, MobileBackground } from "./lib/types";
import { useGlucoseSocket } from "./hooks/useGlucoseSocket";
import { useNow } from "./hooks/useNow";
import { formatReminderTime } from "./lib/reminderLabel";
import { useReminderOverlay } from "./hooks/useReminderOverlay";
import { useSettings } from "./hooks/useSettings";
import { useThresholds } from "./hooks/useThresholds";
import { useTTS } from "./hooks/useTTS";
import { useVoiceAgent } from "./hooks/useVoiceAgent";
import { useVoiceDispatch } from "./hooks/useVoiceDispatch";
import { useVoiceCommands } from "./hooks/useVoiceCommands";
import { cancelPreview } from "./lib/alarmPreview";
import * as devicesApi from "./api/devices";
import * as membersApi from "./api/members";
import * as remindersApi from "./api/reminders";
import {
  getBearer,
  getDeviceId,
  getDeviceToken,
  getDeviceTypeForVariant,
  getOrCreateClientId,
  isKiosk,
  setKiosk,
  wasSignedOut,
} from "./lib/auth";
import {
  getDeviceUserAgent,
  initCapacitorChrome,
  wireWidgetDeepLink,
  capacitorPlatform,
} from "./lib/capacitor";
import { isAndroidTabletClassRuntime } from "./lib/deviceClass";
import * as kioskOta from "./lib/kioskOta";
import { alarmOutranksOverlay } from "./lib/alarmOutranksOverlay";
import { playWakeEarcon } from "./lib/earcon";
import { bootstrapNativePush } from "./lib/nativePush";
import { sweepRecoveredCards } from "./lib/recoveredCardSweep";
import { useAlarmRouteReturn } from "./hooks/useAlarmRouteReturn";
import { useLogOverlay } from "./hooks/useLogOverlay";
import { showAppChrome } from "./lib/appChrome";
import { treatingSurfaceMounted } from "./lib/treating";
import { useActivationGuard } from "./hooks/useActivationGuard";
import {
  isAvailable as alarmReliabilityAvailable,
  getStatus as getAlarmReliabilityStatus,
  getIosStatus as getIosAlarmReliabilityStatus,
  isAndroidPhoneNative,
  isIosPhoneNative,
} from "./lib/alarmReliability";
import { unmetCountOf as alarmReliabilityUnmetCount } from "./hooks/useAlarmReliability";
import { isAlarmTourDone, launchAlarmTour } from "./lib/alarmTour";
// Cycle 464 — the subscription state store + the free-month step gate.
import {
  SubscriptionProvider,
  refetchSubscriptionFresh,
  useSubscription,
  useSubscriptionStepGate,
} from "./hooks/useSubscription";
import { GraceBanner } from "./components/ui/GraceBanner";
import { KioskLockout } from "./components/kiosk/KioskLockout";
import { LockoutScreen } from "./screens/lockout/LockoutScreen";
import { isKioskSurface } from "./lib/billing";
import { lockoutCoversPath, ownerLocked } from "./lib/serviceLock";
import AlarmOnboardingTour from "./screens/onboarding/AlarmOnboardingTour";
import AvatarNudge from "./screens/onboarding/AvatarNudge";
import ActivationPrompt from "./screens/onboarding/ActivationPrompt";
import { endLiveActivity, ensureLiveActivity } from "./lib/liveActivity";
// Cycle 212: the widget snapshot write/clear now go through the
// per-platform dispatcher (iOS App Group + Android `standard`
// SharedPreferences). The reading-driven effect below is unchanged —
// only the import source moved.
import {
  clearGlucoseWidgetSnapshot,
  writeGlucoseWidgetSnapshot,
} from "./lib/glucoseWidget";
import { selectCurrentGlucoseFor } from "./lib/currentGlucose";
import { nativeGlanceSeed } from "./lib/nativeGlance";
import {
  composeGlucoseUtterance,
  resolveUtteranceContext,
} from "./lib/utterance";
import { reportVeracityEvent } from "./api/tts";
// Cycle 330: the single source of truth for the quiet-hours overnight wrap
// (shared with useAutoAnnounce — never re-derive it inline).
import { isWithinQuietHours } from "./lib/quietHours";
import { FOREGROUND_GRACE_MS } from "./hooks/useAutoAnnounce";
import { lockPortrait } from "./lib/orientationLock";
import { effectiveBg } from "./lib/mobileBackgrounds";
import { ThemePacksProvider, useThemePacks } from "./lib/themePacksContext";
import { IOB_RING_TOKENS, iobRingColorVars } from "./lib/iob";
import { SettingsProvider } from "./lib/settingsContext";
import { ConfirmProvider } from "./lib/confirmContext";
import { useSuppressNativeMenu } from "./lib/useSuppressNativeMenu";
import { AlertsScreen } from "./screens/alerts/AlertsScreen";
import { DashboardScreen } from "./screens/dashboard/DashboardScreen";
import { ReportsScreen } from "./screens/dashboard/ReportsScreen";
import { HistoryScreen } from "./screens/history/HistoryScreen";
import { HouseholdScreen } from "./screens/household/HouseholdScreen";
import { LogScreen } from "./screens/log/LogScreen";
import { LogOverlay } from "./screens/log/LogOverlay";
import {
  compactAlarmControlVisible,
  isPairingDisplayPath,
  isPairingPath,
  alarmSurfaceNeedsScreen,
  isNowPath,
  lowSideControlOwed,
} from "./lib/alarmControl";
import { PhotoMealScreen } from "./screens/log/PhotoMealScreen";
import { RecentEntriesScreen } from "./screens/log/RecentEntriesScreen";
import { LoginScreen } from "./screens/login/LoginScreen";
import { ResetPasswordScreen } from "./screens/reset/ResetPasswordScreen";
import { ForgotPasswordScreen } from "./screens/forgot/ForgotPasswordScreen";
import { NowScreen } from "./screens/now/NowScreen";
import { KioskPairingScreen } from "./screens/pairing/KioskPairingScreen";
import { PairingApprovePanel } from "./screens/pairing/PairingApprovePanel";
import { SettingsScreen } from "./screens/settings/SettingsScreen";
import { subscribeJoined as subscribeWifiJoined } from "./lib/kioskWifi";
import { routeReturnExempt as routeReturnExemptForWifi } from "./lib/alarmAudible";

// Cycle 92: the admin console is a web/desktop-only surface mounted at
// /admin/*, lazily imported so it's code-split out of the consumer
// bundle. It is NOT a frame variant and never renders kiosk/mobile
// chrome — it owns the whole viewport and runs its own auth gate
// (GET /api/admin/me). A non-admin sees a generic not-found view.
const AdminApp = lazy(() => import("./admin/AdminApp"));

// Cycle 26: tagged into the user_agent of the device-registration call
// so Settings → Devices can distinguish a native Capacitor install from
// a Safari PWA install of the same user. Bump alongside web/package.json
// each cycle.
const APP_VERSION = "0.461.2";

function applyTheme(theme: "dark" | "light"): void {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

function readInitialTheme(): "dark" | "light" {
  // Cycle 348: the mobile Light/Dark toggle is retired — Dark is the sole,
  // permanent mobile appearance, regardless of any value a prior session
  // stored in localStorage. No longer reads it.
  return "dark";
}

// Cycle 84: the variant-keyed appearance contract.
//   kiosk / web → exactly one `theme-*` class from `display_theme`,
//                 `.dark` ignored/removed.
//   mobile      → `.dark` unconditionally (cycle 348 — was per
//                 `display_dark_mode`; that field is still read/written on
//                 the wire but no client applies it anymore).
// `midnight` is token-identical to `.dark`, so the kiosk/web default is
// a pixel no-op vs the pre-cycle dark look (and there's no flash from
// the pre-auth `.dark` bootstrap → `theme-midnight` swap).
const THEME_CLASSES = [
  "theme-midnight",
  "theme-deep-sea",
  "theme-evergreen",
  "theme-twilight",
  "theme-slate",
  "theme-lagoon",
] as const;

function applyAppearance(
  variant: FrameVariant,
  theme: DisplayTheme,
  // Cycle 171: the mobile-only selectable app background id.
  mobileBg: MobileBackground = "aurora",
): void {
  const root = document.documentElement;
  for (const c of THEME_CLASSES) root.classList.remove(c);
  if (variant === "kiosk" || variant === "web") {
    root.classList.remove("dark");
    root.classList.add(`theme-${theme}`);
    // Cycle 171: data-bg is mobile-only — never leak the catalog to
    // web/kiosk (their appearance is byte-identical).
    root.removeAttribute("data-bg");
  } else {
    // mobile — Cycle 348: Dark is the sole, permanent appearance (the
    // Light/Dark toggle is retired). Always dark, regardless of any
    // stored `display_dark_mode` value.
    root.classList.add("dark");
    // Cycle 171: drive the MobileBackground layer's per-preset tokens.
    // "aurora" is harmless (no [data-bg="aurora"] block → the component
    // renders the default aurora from the bare --aurora-* ramp).
    root.setAttribute("data-bg", mobileBg);
  }
}

/** Capture `?kiosk=1` once and persist to localStorage so reboots stick. */
function useKioskFlagBootstrap(): void {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("kiosk") === "1") setKiosk(true);
    if (params.get("kiosk") === "0") setKiosk(false);
  }, [location.search]);
}

// Cycle 210: wrap the entire app — every surface, including the /admin
// console, the unauthenticated login / pair / reset screens, and the
// authenticated shell — in the app-styled confirm/alert provider (so no
// native `window.confirm`/`alert` dialog ever shows) and mount the global
// selection / context-menu suppressor. The suppressor reads the explicit
// kiosk flag (`isKiosk()`): a paired Pi / Android-tablet / iPad kiosk sets
// it, so the kiosk's unconditional suppression (including inside inputs)
// is correct, while web / mobile keep the native paste menu in fields.
export default function App(): JSX.Element {
  useSuppressNativeMenu(isKiosk());
  return (
    <ConfirmProvider>
      <AppInner />
    </ConfirmProvider>
  );
}

function AppInner(): JSX.Element {
  const auth = useAuth();
  const location = useLocation();

  useKioskFlagBootstrap();

  // Theme bootstrap on first render. Cycle 26 also kicks off the
  // Capacitor chrome init (status-bar style + appUrlOpen listener)
  // here so the dark-navy splash → SPA hand-off doesn't flash white
  // on native. Both calls no-op on every non-Capacitor surface.
  useEffect(() => {
    applyTheme(readInitialTheme());
    void initCapacitorChrome();
  }, []);

  // Cycle 92: the /admin/* surface is handled entirely outside the
  // consumer shell (no frame variant, no CGM gate, its own auth probe).
  // Branch before the auth-status splash / signed-out routes so a deep
  // link to /admin doesn't get redirected to /login or /pair — the
  // admin gate (GET /api/admin/me) decides access for itself.
  if (
    location.pathname === "/admin" ||
    location.pathname.startsWith("/admin/")
  ) {
    return (
      <Suspense fallback={<div style={{ minHeight: "100vh", background: "var(--bg)" }} />}>
        <AdminApp />
      </Suspense>
    );
  }

  // Cycle 95: the public password-reset page. Unauthenticated by design
  // (a locked-out user follows an admin-issued link), so branch before
  // the auth-status splash / signed-out kiosk-vs-login redirect. Not part
  // of the kiosk shell.
  //
  // Cycle 453.2 (operator decision 2): bare ONLY when no session is signed
  // in. A signed-in session reaches this page inside the authenticated
  // shell below, where the alarm layer (sound, the frame's No Data banner,
  // the off-Now alarm control) keeps covering it. Nothing about the page's
  // own flow changes; the alarm layer never reads its token or query string.
  if (location.pathname === "/reset-password" && auth.status !== "signedIn") {
    return <ResetPasswordScreen />;
  }

  // Cycle 190: the public self-serve "forgot password" request page.
  // Unauthenticated by design (a locked-out, signed-out user reaches it
  // directly), so branch alongside /reset-password before the auth-status
  // splash / kiosk-vs-login redirect. Web + mobile; not part of the kiosk
  // shell.
  // Cycle 453.2: bare only when signed out, exactly like /reset-password.
  if (location.pathname === "/forgot-password" && auth.status !== "signedIn") {
    return <ForgotPasswordScreen />;
  }

  // While the auth status is unknown, show a blank logo splash — it lasts
  // milliseconds in practice but avoids a jarring flash to /login.
  if (auth.status === "unknown") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          color: "var(--ink-3)",
        }}
      >
        <Logo size={112} />
        <span style={{ fontWeight: 600, letterSpacing: "-0.01em" }}>Sugar Alarm</span>
      </div>
    );
  }

  // Unauthenticated routes — note the kiosk default lands on /pair, the
  // web default lands on /login.
  if (auth.status === "signedOut") {
    // Cycle 281: during Early Access, a plain web browser can't sign in
    // (web access is subscription-gated, not yet live). The native app and
    // any kiosk pass the gate untouched — `webAccessAllowed` is fail-open and
    // only blocks a browser with no app/kiosk/device/override signal. Gating
    // the signed-OUT state only: an existing session is never interrupted.
    if (!webAccessAllowed()) {
      return <WebGate />;
    }
    return (
      <Routes>
        <Route path="/login" element={<LoginScreen auth={auth} />} />
        {/* Signed-out: rendered outside the kiosk frame (no header, rail or
            No Data banner here), so full screen height — fitFrame={false}. */}
        <Route path="/pair" element={<KioskPairingScreen fitFrame={false} />} />
        {/* Cycle 331: the onboarding "pair this device to a household"
            entry (a card on the CGM-provider screen) reaches this same
            code-display screen pre-auth too — an anonymous visitor may
            tap it before ever signing in. Always force-display (never
            the JWT-only approve nudge); back returns to the provider step. */}
        <Route
          path="/pair-device"
          element={
            <KioskPairingScreen forceDisplay fitFrame={false} backTo="/login" backLabel="Back" />
          }
        />
        {/* The kiosk default is the pairing-code display (a fresh, never
            provisioned unit shows a code for the phone) — EXCEPT right
            after an explicit Log out, which must always land on the sign-in
            screen. `wasSignedOut()` is set only by `useAuth.signOut` and
            cleared by the next sign-in / completed pairing, so a cold boot
            and a revoked-token teardown both keep the /pair default.
            Pairing stays one tap away from /login (the provider step's
            "pair this device" card → /pair-device). */}
        <Route
          path="*"
          element={
            <Navigate
              to={isKiosk() && !wasSignedOut() ? "/pair" : "/login"}
              replace
            />
          }
        />
      </Routes>
    );
  }

  // Authenticated. The /login and /pair-approve routes are hosted inside
  // the same shell so the user can navigate to them without losing chrome.
  // If the user just signed in but has no CGM, send them through the
  // login flow's connect step — except on /pair-approve, which is the
  // phone-side kiosk pairing screen and must work before any CGM is
  // linked (the kiosk can be paired first, CGM linked later), and
  // /pair-device (cycle 331), the same onboarding pair-device entry
  // reached from the CGM-provider screen for an already-authenticated,
  // not-yet-CGM-linked session.
  const needsCgm = !auth.user?.cgm_provider;
  if (
    needsCgm &&
    location.pathname !== "/login" &&
    location.pathname !== "/pair-approve" &&
    location.pathname !== "/pair-device" &&
    // Cycle 453.2 (operator decision 2): exactly these two routes, and
    // nothing else, are exempt — a signed-in account with no CGM linked
    // yet must still be able to finish a password reset it started.
    location.pathname !== "/reset-password" &&
    location.pathname !== "/forgot-password"
  ) {
    return <Navigate to="/login" replace />;
  }

  // Wrap the authenticated tree in the settings provider so every
  // screen can read `units` / thresholds from one place.
  // Cycle 435 (security re-run): keyed on the ACCOUNT, like the member
  // identity and the socket — a sign-in as someone else happens inside
  // this same mounted shell, and these settings carry the thresholds and
  // the alarm-enable flag that judge the number.
  return (
    <SettingsProvider enabled accountKey={auth.user?.id ?? null}>
      {/* Cycle 174: theme-pack ownership context — one source of truth for
          the background gating + render fallback + the picker. */}
      <ThemePacksProvider>
        <AuthenticatedShell auth={auth} />
      </ThemePacksProvider>
    </SettingsProvider>
  );
}

function AuthenticatedShell({ auth }: { auth: ReturnType<typeof useAuth> }) {
  const tts = useTTS();
  const now = useNow(15_000);
  const variant = useFrameVariant();
  // Cycle 100 — kiosk voice agent (Porcupine wake loop on 127.0.0.1:8765).
  // `available` is false on every non-kiosk surface and on a kiosk whose
  // agent is unreachable / built without the access key + `.ppn` files,
  // so web/mobile make zero agent calls and render no voice UI.
  const voice = useVoiceAgent(variant);
  // Cycle 33: poll `/api/health.deploy.app_version` on a 60 s cadence
  // and fire a cache-busting `location.replace(...)` when the value
  // changes. Combined with the server-side `Cache-Control: no-cache`
  // on `/` + `index.html` fall-through, this makes Fly redeploys
  // auto-reach every kiosk on its next poll tick without manual
  // `systemctl restart sugalarm-kiosk`. Mounted at the authenticated
  // shell so it sleeps on /login and /pair.
  useAppVersionPoller({ enabled: auth.status === "signedIn" });
  const location = useLocation();
  const nav = useNavigate();
  // Cycle 473.2 (operator decision 2026-11-05, item 3): after a successful
  // Wi-Fi change ONLINE the kiosk APK says "wifiJoined" and the tablet lands
  // on Now by an in-app route change — never a reload, so a sounding alarm
  // is never cut. (Offline joins reload to the root, which lands on Now.)
  // Not from the pairing screens (a handshake broken mid-flow strands a
  // device — the 451.1/452 exemption), and not when already on Now (a push
  // would close an open Log entry); a replace, so Back never steps Now → Now.
  const wifiJoinPathRef = useRef(location.pathname);
  wifiJoinPathRef.current = location.pathname;
  useEffect(
    () =>
      subscribeWifiJoined(() => {
        const here = wifiJoinPathRef.current;
        if (here === "/now" || routeReturnExemptForWifi(here)) return;
        nav("/now", { replace: true });
      }),
    [nav],
  );
  const settingsCtx = useSettings();
  const units = settingsCtx.units;

  // Cycle 84: apply the variant-keyed appearance (kiosk/web theme class OR
  // — cycle 348 — mobile's now-permanent Dark) once settings load and
  // whenever the theme / variant changes. A `settings_update` broadcast
  // (live PATCH from any surface) flows through the settings context, so
  // the theme class swaps with no extra plumbing. We wait for settings to
  // load — until then the pre-auth `.dark` bootstrap holds, which is
  // token-identical to `theme-midnight`, so there's no flash.
  const displayTheme = settingsCtx.settings?.display_theme ?? "midnight";
  // Cycle 348: `display_dark_mode` is no longer read here — the Light/Dark
  // toggle is retired and mobile is dark-only regardless of its stored
  // value (see applyAppearance's mobile branch).
  // Cycle 171: the mobile-only selectable app background (default aurora).
  // Cycle 174: render the EFFECTIVE preset — a premium preset whose pack
  // the account doesn't (yet/any longer) own falls back to aurora, so a
  // revoked entitlement never breaks the app (the stored selection is
  // untouched and re-renders once re-owned).
  const {
    ownedPackIds,
    packOf,
    refresh: refreshThemePacks,
    loaded: themePacksLoaded,
    offeredPresetIds,
  } = useThemePacks();
  const selectedBg = settingsCtx.settings?.display_mobile_background ?? "aurora";
  const mobileBg = effectiveBg(selectedBg, ownedPackIds, packOf, {
    loaded: themePacksLoaded,
    offered: offeredPresetIds,
  });
  const settingsLoaded = settingsCtx.settings != null;
  // Cycle 149: while alarm-clock mode is active, FORCE the dark/midnight
  // appearance regardless of the user's theme (a light theme must never
  // render a bright bedside face). Reversible by construction — this
  // effect re-runs when `active` flips and re-applies the user's real
  // theme; `display_theme` itself is never written. Read via the light
  // store hook (the controller mounts later in this component).
  const clockActive = useClockMode().active;
  useEffect(() => {
    if (!settingsLoaded) return;
    applyAppearance(variant, clockActive ? "midnight" : displayTheme, mobileBg);
  }, [variant, displayTheme, settingsLoaded, clockActive, mobileBg]);

  // Cycle 170 web-only light/dark class — RETIRED cycle 348 (the nav-rail
  // toggle that drove it is gone; web is permanently dark, same look as
  // before anyone had picked Light). `applyWebThemeClass` is no longer
  // called from anywhere, so `web-light` is never added regardless of OS
  // `prefers-color-scheme` or any value a session previously stored in
  // `lib/webTheme`'s localStorage key. The module + `WebThemeToggle.tsx`
  // are left in place, unwired, per the operator's explicit "don't tear
  // it out this cycle" direction — a future cleanup cycle's call, not
  // this one's.

  // Cycle 348: portrait-lock every native-mobile screen except the Clock
  // face, which explicitly unlocks itself for the duration it's active
  // (see useAlarmClockMode.ts) and this effect re-locks the instant clock
  // mode exits below it in this same component. Fires once variant
  // resolves to "mobile"; `lockPortrait()` no-ops on web/kiosk/a
  // non-native browser by construction, so this is safe to call
  // unconditionally on every variant.
  useEffect(() => {
    if (variant !== "mobile") return;
    void lockPortrait();
  }, [variant]);

  // Cycle 118: the IOB ring-color override applier — the single seam.
  // The two settings drive the two theme-invariant `--iob-ring-*` tokens
  // (cycle 104d) as inline custom properties on the document root; every
  // IOB color surface (ring arcs, ring-matched countdown timers,
  // MealContextLines dots, ActiveBolusesModal dots, the IobPager page-2
  // tags) already reads those tokens via MEAL_BOLUS_COLOR /
  // CORRECTION_BOLUS_COLOR or the dot classes, so no call-site changes are
  // needed. A null setting removes the inline override → the stock
  // tokens.css value (#355e3b / #ffd60a) is what cascades. Re-applies on
  // every settings change (initial load, after a PATCH, after an
  // active-member switch), since it keys off the settings-context values.
  const iobMealColor = settingsCtx.settings?.iob_meal_color ?? null;
  const iobCorrectionColor = settingsCtx.settings?.iob_correction_color ?? null;
  // Cycle 147: the third (basal) ring color.
  const iobBasalColor = settingsCtx.settings?.iob_basal_color ?? null;
  useEffect(() => {
    // Cycle 118b: the field→token mapping lives in `iobRingColorVars`
    // (lib/iob.ts) — the same seam the Settings preview wrapper uses — so
    // the applier and preview can never disagree on which color drives
    // which ring. Set the tokens present in the bag; remove the rest
    // (falls back to the stock tokens.css values).
    const root = document.documentElement;
    const vars = iobRingColorVars({
      meal: iobMealColor,
      correction: iobCorrectionColor,
      basal: iobBasalColor,
    });
    for (const token of IOB_RING_TOKENS) {
      if (vars[token]) root.style.setProperty(token, vars[token]);
      else root.style.removeProperty(token);
    }
  }, [iobMealColor, iobCorrectionColor, iobBasalColor]);

  const bearer = getBearer();
  // Cycle 435 (security review): keyed on the ACCOUNT, so an in-place
  // sign-in as a different account clears the member identity at once.
  const activeMember = useActiveMember(auth.status === "signedIn", auth.user?.id ?? null);
  // Cycle 236: a fired reminder shows the fullscreen <ReminderOverlay> (the
  // cycle-76 dismissible banner is retired). Cycle 359: the overlay is
  // Close-only — the hook no longer owns a snooze timer.
  const reminderOverlay = useReminderOverlay();
  const { activeReminder, activeFire, setActiveReminder, applyRemoteDismiss } =
    reminderOverlay;
  const socket = useGlucoseSocket(
    auth.status === "signedIn" ? bearer : null,
    activeMember.active?.member_id ?? null,
    useMemo(
      () => ({
        onSettingsUpdate: (next) => settingsCtx.applyRemoteUpdate(next),
        onActiveMemberChanged: () => {
          void activeMember.refresh();
        },
        onMemberUpdate: () => {
          void activeMember.refresh();
          // Cycle 305: a `member_update` frame carries the member's current
          // `cgm_provider` (null after a disconnect on another device). The
          // Settings → CGM connection card reads a local `GET /api/cgm/status`
          // snapshot that has no WS wiring, so nudge it via a window event
          // (the cycle-217 reminders-pane pattern) — the pane refetches and
          // flips to "not connected" without a reload. Cheap + idempotent;
          // the light status refetch is harmless for non-CGM member updates.
          window.dispatchEvent(new CustomEvent("sugalarm:cgm-changed"));
        },
        onReminderFired: (reminder, fire) => {
          // Cycle 236: show the fullscreen takeover. TTS fires from the
          // overlay-appearance effect below, not here.
          setActiveReminder(reminder, fire ?? null);
        },
        // Cycle 217: a reminder was created/edited/deleted on another of
        // the owner's devices. Re-broadcast as a window event the
        // Settings → Reminders pane listens for (it owns its own list +
        // is mounted deep in Settings) so the edit appears across all
        // devices instantly.
        onRemindersChanged: () => {
          window.dispatchEvent(new CustomEvent("sugalarm:reminders-changed"));
        },
        // Cycle 272/359: a fired reminder was Closed on another of the owner's
        // devices — clear the overlay here too, in lockstep. (The cross-device
        // `reminder_snoozed` frame is now a dormant no-op — the reminder Snooze
        // was removed; see useGlucoseSocket.ts.)
        onReminderDismissed: (reminderId, fireId) => {
          applyRemoteDismiss(reminderId, fireId ?? null);
        },
        // Cycle 192: the published theme catalog changed somewhere (admin
        // publish/edit/Core hide-restore) — re-fetch this account's catalog
        // so the Settings picker + the live MobileBackground render path
        // pick it up; a now-unavailable current selection falls back to
        // aurora via effectiveBg without a relaunch.
        onThemeCatalogUpdated: () => {
          void refreshThemePacks();
        },
      }),
      [
        settingsCtx.applyRemoteUpdate,
        activeMember.refresh,
        refreshThemePacks,
        setActiveReminder,
        applyRemoteDismiss,
      ],
    ),
  );
  // Cycle 90 hotfix: settings are PER-MEMBER, but the switch handlers
  // (Household + Settings member switcher, and the remote
  // `active_member_changed` frame) only refresh `useActiveMember` — not
  // the settings context. Without this, switching the active member left
  // the PREVIOUS member's settings in place (units, thresholds, theme,
  // and the selected `display_background_id`), so e.g. Member D's selected
  // background bled onto the member's surface after switching back to the member. This
  // is the single seam that re-pulls /api/settings (which resolves the
  // now-active member server-side) on a genuine member→member switch.
  // The ref skips the initial null→first load (the SettingsProvider's own
  // mount fetch already covers that) and sign-out (→ null).
  const activeMemberId = activeMember.active?.member_id ?? null;
  const prevMemberIdRef = useRef<string | null>(null);
  const settingsRefresh = settingsCtx.refresh;
  useEffect(() => {
    const prev = prevMemberIdRef.current;
    prevMemberIdRef.current = activeMemberId;
    if (prev && activeMemberId && prev !== activeMemberId) {
      void settingsRefresh();
    }
  }, [activeMemberId, settingsRefresh]);

  // Alarm episode state machine (cycle 20.x). Drives the
  // "Address the Situation" → 5-min snooze → "Silence" flow for low /
  // urgent_low / urgent_high readings. Triggers off the current
  // reading's effective level (server-side alerts are edge-triggered
  // so a sustained low wouldn't generate a fresh frame).
  // Cycle 35: thresholds resolve through `useThresholds()` — the same
  // hook every zone-aware renderer reads from, so the banner, hero,
  // pill, sparkline, and chart all agree.
  const alarmsEnabled = settingsCtx.settings?.alerts_enabled !== false;
  const thresholds = useThresholds();
  // Cycle 310: the last wedge event (a latched-past-recovery episode the
  // server-truth reconciliation cleared). Held in a ref and relayed on
  // every OTA context push below — fleet telemetry only.
  const lastWedgeRef = useRef<{
    atMs: number;
    episodeAgeSeconds: number;
    activeMemberNull: boolean;
  } | null>(null);
  const episode = useAlarmEpisode({
    enabled: alarmsEnabled,
    socket,
    thresholds,
    onWedgeCleared: (info) => {
      lastWedgeRef.current = {
        atMs: Date.now(),
        episodeAgeSeconds: info.episodeAgeSeconds,
        activeMemberNull: info.activeMemberNull,
      };
    },
  });
  // Cycle 242: the five per-zone custom-sound setting ids, fed to the alarm
  // owner so each zone's <audio> src tracks the user's selection (synced via
  // the settings context). Unknown ids resolve to the zone default in the
  // hook; absent → "default".
  const soundSelection = useMemo(
    () => ({
      urgent_low: settingsCtx.settings?.alarm_sound_urgent_low ?? "default",
      low: settingsCtx.settings?.alarm_sound_low ?? "default",
      inrange: settingsCtx.settings?.alarm_sound_inrange ?? "default",
      high: settingsCtx.settings?.alarm_sound_high ?? "default",
      urgent_high: settingsCtx.settings?.alarm_sound_urgent_high ?? "default",
    }),
    [
      settingsCtx.settings?.alarm_sound_urgent_low,
      settingsCtx.settings?.alarm_sound_low,
      settingsCtx.settings?.alarm_sound_inrange,
      settingsCtx.settings?.alarm_sound_high,
      settingsCtx.settings?.alarm_sound_urgent_high,
    ],
  );

  // Cycle 360.5: a bounded sensor-pause hold (cycle 354) stands the kiosk
  // no-data loop down (planned outage). Recomputed against the 15s app clock
  // so an expiring hold re-enables the alarm within a tick. Also gates only
  // the loop — never a glucose alarm (same as the push-page rail).
  const sensorPauseActive = useMemo(() => {
    const sp = settingsCtx.settings?.sensor_pause;
    if (!sp) return false;
    const until = new Date(sp.until).getTime();
    return Number.isFinite(until) && until > now;
  }, [settingsCtx.settings?.sensor_pause, now]);

  // Cycle 468: THIS account's lock, from the server's own answer (the
  // stamp, never a clock). The WS `service_state` frame for one of this
  // account's members is the prompt to re-read it, so the lockout lands
  // (and lifts) within a frame rather than at the next 10-minute refetch.
  const subscriptionStore = useSubscription();
  const ownerLockedOut = ownerLocked(subscriptionStore.data);
  // The active member belongs to THIS account (the owner's own household):
  // for it the owner's own subscription read is authoritative over the
  // per-member WS map (a lost or stale frame never un-locks the owner's
  // kiosk or re-arms its No Data alarm — safety review round 2, M-1/L-c).
  const activeIsOwn =
    activeMember.fullMember?.owner_user_id != null &&
    activeMember.fullMember.owner_user_id === (auth.user?.id ?? null);
  // For the owner's OWN member the REST read is the truth in BOTH directions
  // (safety review round 3, M-2): a stale WS `locked` left by a race between
  // the sweep's lock and a purchase must never keep the owner's number
  // hidden or its No Data alarm silenced. A followed member reads the map.
  const activeMemberLocked = activeIsOwn ? ownerLockedOut : socket.serviceLocked;
  useEffect(() => {
    if (auth.status !== "signedIn") return;
    // A read that STARTS now (astra round 2): one already in flight may have
    // left before the lock this frame announces was committed.
    void refetchSubscriptionFresh();
    // The belt behind the server's after-commit announce (safety review
    // M1): a frame that raced the grant's commit is re-read once more a
    // few seconds later, so a lockout never lingers until the 10-minute
    // refresh on a surface that never fires `visibilitychange` (the kiosk).
    const t = setTimeout(() => void refetchSubscriptionFresh(), 3000);
    return () => clearTimeout(t);
  }, [auth.status, socket.serviceLockedByMember]);

  const alarms = useAlarmSounds({
    enabled: alarmsEnabled,
    socket,
    activeMemberId: activeMember.active?.member_id ?? null,
    episode,
    thresholds,
    soundSelection,
    // Cycle 360.5: the looping in-app no-data alarm is KIOSK-ONLY (the
    // always-foregrounded tablet; phones get the cycle-360 critical push).
    noDataLoopEnabled: variant === "kiosk",
    // Cycle 468: a locked member's quiet feed is the lock, not an outage.
    serviceLocked: activeMemberLocked,
    sensorPauseActive,
    // Cycle 374: the urgent-low siren no longer loops anywhere. On the
    // KIOSK it re-fires on an escalating interval instead — the pinned
    // floor, because that is the one surface guaranteed to be in the room
    // and the one with no push or phone call behind it.
    kioskUrgentRepeat: variant === "kiosk",
    // Cycle 391: an open treating window replaces the kiosk cue with one
    // soft tick every five minutes. Passed for every variant; the hook
    // only acts on it when `kioskUrgentRepeat` is also true, so phones
    // and the web SPA genuinely go quiet for the window while the kiosk —
    // the surface guaranteed to be in the room — never does.
    treatingActive: episode.treating,
    // Cycle 381: while the server says it is calling this household, skip
    // the redundant in-app urgent-low one-shot on the PHONE — it plays the
    // same audio file the critical notification just played, and landed on
    // top of the ringing call. Scoped to the active member; absent/expired
    // means no suppression (fail-open, toward sound).
    voiceCallQuietUntil: socket.activeMemberId
      ? socket.voiceCallQuietUntilByMember[socket.activeMemberId] ?? null
      : null,
    // Cycle 403: while the server says we are on the phone with THIS
    // account, cut this session's in-app alarm audio — the live human
    // voice beats the chime, and the person hearing it is the one we are
    // literally talking to. Keyed on the signed-in USER, which is the
    // whole point of the cycle: contacts are phone numbers, push tokens
    // are accounts, and `emergency_contacts.linked_user_id` is the join
    // that finally lets the server name the handset it is ringing.
    //
    // The KIOSK is excluded inside the hook (`callQuietActive` returns
    // false when `kioskUrgentRepeat` is set) rather than here, because a
    // kiosk device session authenticates AS the owner — so this id would
    // match and only the variant guard protects the never-silent floor.
    // Absent/expired ⇒ no suppression, which is the fail-open direction.
    // Cycle 406: read under the account+member key — the ACTIVE member's
    // deadline only, so "the member's quiet never covers Ben" holds on the phone.
    callQuietUntil: (() => {
      const key = callQuietKey(auth.user?.id, socket.activeMemberId);
      return key ? socket.callQuietUntilByUser[key] ?? null : null;
    })(),
  });

  // Cycle 145: alarm-clock-mode controller — the single mount. Reads
  // only the persisted schedule + whether an episode is live (to
  // suspend night dim); it never touches the alarm hooks above. The
  // face renders inside NowScreen BELOW the alarm surfaces.
  const clockMode = useAlarmClockMode(
    settingsCtx.settings?.alarm_clock ?? null,
    episode.isLive,
  );
  // Cycle 402: THE BEDSIDE FACE IS ACTUALLY ON SCREEN — clock mode being
  // enabled is NOT the same thing, because the face only renders on `/now`
  // (a user can have clock mode on and be sitting in Settings). This is the
  // single source for that fact: `showChrome` subtracts it, and the kiosk
  // frame uses it to keep the no-data banner alive through the chrome
  // suppression and give it the face's fixed-bottom treatment. Deriving it
  // twice is how the mount gate and the band reservation drift apart.
  // ⚠ CYCLE 451 — "THE FACE IS ON SCREEN", NOT "CLOCK MODE IS ON". The
  // bedside face is one branch of `NowScreen`, and the surfaces above it
  // early-return in front of it. The treating screen is one of them
  // (cycle 407), and it is NOT a surface that may hide the app chrome —
  // a caregiver holding a ten-minute window has to be able to reach
  // History and come back. Between cycle 407 and this one, a declared
  // window inside a scheduled bedside window inherited the face's
  // suppression: no header, no rail, no tab bar, and a band of wallpaper
  // where `.kioskContent`'s bottom-nav padding showed through. The
  // subtraction reads the treating screen's OWN mount gate, so the two
  // cannot drift.
  //
  // ⚠ ONLY THE TREATING SCREEN IS SUBTRACTED. The other surfaces that
  // replace the face — the urgent takeover and the cycle-410 lead
  // takeover — are deliberately full-bleed alarm surfaces and keep the
  // suppression they have always had. Widening this to "any surface
  // above the face" would bring the chrome back under a live alarm
  // takeover, which nobody asked for and nothing has measured.
  const clockFaceActive =
    clockMode.active &&
    location.pathname === "/now" &&
    !treatingSurfaceMounted(episode);

  // Activating clock mode (nav button / schedule / Settings) brings the
  // app to /now where the face renders; one-shot on activation so later
  // navigation (e.g. a push deep-link) is never fought.
  useEffect(() => {
    if (clockMode.active && location.pathname !== "/now") {
      nav("/now");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockMode.active]);

  // Push the latest preferred voice into the TTS hook each time the
  // settings context updates. Done via an imperative `setVoice` so the
  // hook isn't part of every parent re-render's dep chain.
  useEffect(() => {
    tts.setVoice(settingsCtx.settings?.tts_voice ?? null);
  }, [settingsCtx.settings?.tts_voice, tts]);

  // Cycle 28: bootstrap native push once per authenticated session
  // when running inside the Capacitor shell AND the operator has
  // previously enabled native push in Settings → Notifications. The
  // helper no-ops on every non-Capacitor surface and when the local
  // enabled flag isn't set, so kiosk / browser / PWA never see the
  // permission prompt. Tap handler navigates to `/alerts?id=<id>` so
  // the highlight effect in AlertsScreen picks the row up.
  useEffect(() => {
    if (auth.status !== "signedIn") return;
    let teardown: (() => void) | null = null;
    void bootstrapNativePush({
      onAlertId: (alertId) => {
        nav(`/alerts?id=${encodeURIComponent(alertId)}`);
      },
    }).then((fn) => {
      teardown = fn;
    });
    return () => {
      if (teardown) teardown();
    };
  }, [auth.status, nav]);

  // Cycle 439: clear any "🟢 RECOVERED" card that has outlived its shelf
  // life (30 min by default, carried on the card as `expires_in`). The
  // server CANNOT expire a delivered iOS notification — only app code
  // can — so this runs on open and on every return to the foreground.
  //
  // This is the FLOOR, not the fix: a locked phone nobody touches is
  // cleared by the native sweep on the cycle-356 silent push. Both halves
  // select on `kind === "recovered"`, which no alarm card carries, so
  // neither can reach one. iOS-only and no-op everywhere else; every
  // failure is swallowed inside the helper.
  useEffect(() => {
    if (auth.status !== "signedIn") return;
    void sweepRecoveredCards();
    if (typeof document === "undefined") return;
    const onVis = () => {
      if (document.visibilityState === "visible") void sweepRecoveredCards();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [auth.status]);

  // Cycle 365: activation guard. Audits whether this install can actually
  // be paged, silently arms native push when the OS already granted
  // notifications but the local opt-in flag was never set (the exact way a
  // phone ends up with no token — see lib/activation.ts), and reports the
  // result so the in-app prompt and the admin roster read from the same
  // server verdict. Purely diagnostic: it never gates delivery.
  const handleAlertIdNav = useCallback(
    (alertId: string) => {
      nav(`/alerts?id=${encodeURIComponent(alertId)}`);
    },
    [nav],
  );
  const activation = useActivationGuard({
    token: auth.status === "signedIn" ? bearer : null,
    appVersion: APP_VERSION,
    onAlertId: handleAlertIdNav,
  });

  // Cycle 211: a tap on the home-screen glucose widget opens the app via
  // `sugalarm://now` → route to the Now screen. Always-on (independent of
  // the push opt-in that gates the alerts deep-link above); no-ops off the
  // iOS shell.
  useEffect(() => {
    let teardown: (() => void) | null = null;
    void wireWidgetDeepLink(() => nav("/now")).then((fn) => {
      teardown = fn;
    });
    return () => {
      if (teardown) teardown();
    };
  }, [nav]);

  // Cycle 316 — first-run alarm-permissions onboarding tour (iOS + Android
  // mobile shells). Once per device (the tour's own localStorage done flag,
  // stamped on any terminal exit): after login, if any urgent-path
  // permission for THIS platform is unmet, launch the paged tour. This
  // SUPERSEDES the cycle-315 first-run navigate-to-Settings step as the
  // first-run surface; the cycle-315 home banner + Alarm Reliability screen
  // are untouched and remain the persistent nag path. No-ops on kiosk /
  // plain web / builds that predate the plugin (the iOS stub answers
  // supported:false, so old iOS archives never see a tour whose prompts
  // couldn't fire).
  // Cycle 464: the tour holds while the "Start your free month" step is ON
  // SCREEN, or while its answer is still loading (bounded: the read times
  // out at 5 s and a failure owes nothing), so first run reads free month →
  // CGM → alarm tour. ⚠ Never hold on "owed" alone: an owed account that
  // lands anywhere but `/login` never sees the step, and the tour would be
  // held for good (safety review A2). On today's native apps the step is
  // never owed (no Billing plugin until 465/466), so the tour launches
  // exactly as before.
  const subscriptionStep = useSubscriptionStepGate(
    auth.status === "signedIn",
    auth.user,
    variant === "kiosk",
  );
  const holdTourForSubscription = subscriptionStep.stepShowing || subscriptionStep.pending;
  useEffect(() => {
    if (auth.status !== "signedIn") return;
    if (holdTourForSubscription) return;
    if (isAlarmTourDone()) return;
    let cancelled = false;
    if (isAndroidPhoneNative() && alarmReliabilityAvailable()) {
      void getAlarmReliabilityStatus().then((s) => {
        if (cancelled || s == null || !s.supported) return;
        const unmet = alarmReliabilityUnmetCount(s);
        if (unmet != null && unmet > 0) launchAlarmTour();
      });
    } else if (isIosPhoneNative()) {
      void getIosAlarmReliabilityStatus().then((s) => {
        if (cancelled || s == null || s.supported !== true) return;
        if (
          s.notificationsEnabled !== true ||
          s.criticalAlertsAllowed !== true
        ) {
          launchAlarmTour();
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [auth.status, holdTourForSubscription]);

  // Cycle 25: register this browser as a device row so Settings →
  // Devices shows the right device_type for the session. Kiosk
  // sessions get their row via pairing — skip them. Device-token
  // sessions also skip (the pairing flow already created the row).
  const userId = auth.user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    if (getDeviceToken() !== null) return; // kiosk session — pairing owns the row
    if (variant === "kiosk") return; // explicit kiosk flag — same logic
    // Cycle 26: when running inside the Capacitor shell, surface the
    // marker up-front in the device name so the Devices pane can tell
    // a native install from a Safari PWA install of the same user
    // (the 40-char UA slice below would otherwise drop the suffix).
    const ua = getDeviceUserAgent(APP_VERSION);
    const isNative = ua.includes("(Capacitor)");
    const baseName = isNative
      ? ua.includes("iOS")
        ? "iOS app"
        : ua.includes("Android")
          ? "Android app"
          : "Native app"
      : variant === "mobile"
        ? "Phone"
        : "Browser";
    void devicesApi.register({
      client_id: getOrCreateClientId(),
      name: `${baseName} — ${ua.slice(0, 40)}`,
      device_type: getDeviceTypeForVariant(variant),
    });
  }, [userId, variant]);

  // Cycle 200: the Android tablet kiosk self-reports its camera capability
  // once on mount so the food-photo endpoint's softened auth
  // (CurrentUserPhotoCapable) lets its device token through — a self-heal
  // for already-paired tablets with no re-pair / pairing-flow change. Gated
  // on the Android-tablet predicate AND a device-token (kiosk) session;
  // the Pi kiosk and the iPad kiosk never call it (so they stay excluded),
  // and web/mobile JWT sessions never call it (no device context). Idempotent.
  useEffect(() => {
    if (!userId) return;
    if (getDeviceToken() === null) return; // not a paired device session
    if (!isAndroidTabletClassRuntime()) return; // Pi / iPad / phone — excluded
    void devicesApi.selfReportCapabilities({ camera_capable: true });
  }, [userId]);

  // Cycle 225: feed the native OTA self-updater the SPA's live context. The
  // native layer can't read the WebView's device token, route, or alarm
  // state, so we push a snapshot every ~30s (and immediately whenever the
  // alarm episode's liveness flips). `kioskOta.isAvailable()` is true ONLY in
  // the Android kiosk APK (cycle-225 build+); every other surface no-ops.
  // CRITICAL: `alarmActive` is the alarm-safe install gate — since cycle
  // 311 it is LOW-SIDE-scoped (`otaLowSideLive` below; operator policy
  // "Lows kill - Highs do not"): the updater never installs while a
  // low-side episode is live, and a deferred install proceeds the moment
  // this reports it cleared. High-side episodes do not defer installs.
  const otaCtxRef = useRef<
    Omit<kioskOta.KioskOtaContext, "alarmActive" | "audioActive">
  >({
    token: null,
    route: "/",
    lastReadingAt: null,
    lastAlarmAt: null,
    online: true,
    lastWedgeAtMs: null,
    lastWedgeEpisodeAgeSeconds: null,
    lastWedgeActiveMemberNull: null,
  });
  otaCtxRef.current = {
    token: getDeviceToken(),
    route: location.pathname,
    lastReadingAt: socket.lastReading?.timestamp ?? null,
    lastAlarmAt:
      socket.lastAlert?.fired_at ?? socket.lastAlert?.triggered_at ?? null,
    online: typeof navigator !== "undefined" ? navigator.onLine : true,
    // Cycle 310: last-wedge telemetry (see lastWedgeRef above).
    lastWedgeAtMs: lastWedgeRef.current?.atMs ?? null,
    lastWedgeEpisodeAgeSeconds:
      lastWedgeRef.current?.episodeAgeSeconds ?? null,
    lastWedgeActiveMemberNull:
      lastWedgeRef.current?.activeMemberNull ?? null,
  };
  // Cycle 311 — OPERATOR POLICY (the operator, 2026-07-06): "We can never bypass
  // a low alarm. Lows kill - Highs do not." The OTA install gate blocks
  // on LOW-SIDE episodes ONLY (urgent_low / low); a high-side episode no
  // longer defers an install (the ~15s app relaunch costs a high nothing:
  // high audio has been a one-shot chime + hourly cadence since cycle
  // 232, the banner re-renders on reload, and every push/SMS/server rail
  // is untouched). This matches the platform's existing low-side-is-
  // life-critical posture (SMS/voice low-only c136, repeating escalation
  // low-only c228, urgent-low Snooze-only c73). ONLY the install gate
  // reads these flags — alarm display/audio/controls are unaffected.
  // Cycle 311.1 (post-deploy review hardening): OR-in the level the
  // episode OPENED at, so a LOW-opened episode holds the gate through a
  // family jump of the current reading — the "lows can never be
  // bypassed" rule is structural, not analytical. Strictly
  // defers-more/never-less.
  // Cycle 361: hoisted from `otaEpisodeLowSide` to `episodeLowSide` and now
  // SHARED with the reminder-overlay gate below — the "lows can never be
  // bypassed" rule says every alarm-gated surface reuses this exact predicate
  // rather than re-deriving its own low-side test.
  const episodeLowSide =
    episode.episodeLevel === "low" ||
    episode.episodeLevel === "urgent_low" ||
    episode.startLevel === "low" ||
    episode.startLevel === "urgent_low";
  const otaLowSideLive = episode.isLive && episodeLowSide;
  const otaLowSideAudio = episode.audioOn && episodeLowSide;
  // Cycle 361 → 473.1: THE predicate for "an alarm outranks an overlay" —
  // the alarm can actually SOUND (`episode.audioOn`: active and not
  // silenced, any level), OR a LOW-SIDE episode is live at all, muted or
  // not. A Stopped or snoozed HIGH does not count. Named here (above every
  // reader) so the cycle-361 reminder gate and the kiosk Wi-Fi gate read the
  // SAME rule (operator decision, 2026-11-03: a Stopped high can stay open
  // for hours, the server cannot recover it while the tablet is offline, and
  // gating on it would leave a travelling tablet unable to get online).
  const alarmOutranks = alarmOutranksOverlay({
    audioOn: episode.audioOn,
    isLive: episode.isLive,
    lowSide: episodeLowSide,
  });
  useEffect(() => {
    if (!kioskOta.isAvailable()) return;
    const report = () =>
      void kioskOta.reportContext({
        ...otaCtxRef.current,
        alarmActive: otaLowSideLive,
        // Cycle 310: the native deferral cross-check may only install
        // past a stale alarmActive when audio is NOT sounding.
        // Cycle 311: both flags are low-side-scoped (see policy above).
        audioActive: otaLowSideAudio,
        // Cycle 473.1: the cycle-361 predicate (`alarmOutranksOverlay`: audio
        // on at any level, or any low-side episode live) — the native Wi-Fi
        // screens' 10-minute rule and the foreign-prompt bounce read it; a
        // Stopped/snoozed HIGH does not count (operator decision 2). The OTA
        // gate above stays low-side and never reads it.
        episodeLive: alarmOutranks,
        // Cycle 471 (belt): when THIS page holds no device token — a kiosk
        // that signed out, or one not yet paired — the native updater must
        // not keep heartbeating with a token it remembers from before.
        // `reportContext` ignores an empty `token` by design; this field is
        // the explicit "drop it". A paired kiosk always reports `false`.
        clearToken: otaCtxRef.current.token === null,
      });
    report(); // push immediately on mount + whenever alarm liveness flips
    const id = window.setInterval(report, 30_000);
    return () => window.clearInterval(id);
  }, [otaLowSideLive, otaLowSideAudio, alarmOutranks]);

  // Periodic spoken status announcements. Suppressed while a real
  // urgent alarm is active. The active member's display name is
  // threaded in so each tick greets them by name.
  const memberName = activeMember.fullMember?.display_name ?? null;
  // Cycle 105: the header shows the avatar + name only (relationship
  // label removed). The Now-screen hero keeps its own name + YOU chip.
  const memberAvatarId = activeMember.fullMember?.avatar_id ?? null;

  // Cycle 168: iOS Live Activity (Lock Screen / Dynamic Island /
  // CarPlay Dashboard glance tile). Thin bridge sync — no-ops on
  // every surface except the iOS Capacitor shell (the manager gates
  // internally on window.Capacitor). Starts/refreshes the activity
  // for the ACTIVE member seeded from the latest reading (the
  // server's push updates are primary; the seed just paints the first
  // frame), registers the 17.2+ push-to-start token, replaces on
  // member switch, ends on sign-out, and ends — rather than freezes —
  // when the feed has been stale past the tile's window. Display-only
  // (cycle-73 invariant): nothing here touches the alarm engine.
  const laMemberId = activeMember.active?.member_id ?? null;
  useEffect(() => {
    if (auth.status !== "signedIn") {
      void endLiveActivity();
      // Cycle 211: signed out → the widget shows the "Open Sugalarm"
      // placeholder, not a stale number.
      void clearGlucoseWidgetSnapshot();
      return;
    }
    if (!laMemberId) return;
    const sync = () => {
      // ⚠ Cycle 435: the native glance is seeded THROUGH the selector. It
      // used to read `socket.lastReading` and judge it by timestamp alone —
      // no stream confirmation, no server verdict — and the widget is fed
      // from here, so it was never exempt. The selector samples the clock
      // at this call; the seed rule itself is pure (`lib/nativeGlance.ts`).
      const now = Date.now();
      const view = selectCurrentGlucoseFor(socket, laMemberId, now);
      const decision = nativeGlanceSeed(
        view,
        {
          memberId: laMemberId,
          memberName: memberName ?? "",
          units,
          thresholds,
          // Cycle 211: delta + recent points for the widget snapshot, from
          // the SAME socket feed the tile uses so the two can never disagree.
          // Cycle 435 (security review, LOW): `laMemberId` leads the
          // reducer's active member by one dispatch on a switch; the
          // history is the reducer's active mirror, so hand it over only
          // when the two agree — never one member's number beside
          // another's delta and sparkline.
          recentReadings:
            socket.activeMemberId === laMemberId ? socket.recentReadings : [],
        },
        now,
      );
      // Stale past the tile window (the server's stale-date horizon):
      // end the Live Activity instead of showing a frozen number. The
      // widget is NOT cleared here — its own timeline self-ages to grey
      // (the 30-min staleness ceiling is well within this 35-min gate),
      // so it stays signed-in rather than dropping to the placeholder.
      if (decision.endLiveActivity) {
        void endLiveActivity();
        return;
      }
      void ensureLiveActivity(decision.seed);
      // Cycle 211: forward the same reading to the home-screen widget.
      // Separate from the Live Activity (not gated on whether Live
      // Activities are enabled), so the widget updates regardless.
      void writeGlucoseWidgetSnapshot(decision.seed);
    };
    sync();
    const onVis = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [
    auth.status,
    laMemberId,
    memberName,
    units,
    thresholds,
    // Cycle 435: re-seed when the active member's readings, confirmation
    // or server verdict change — the inputs the selector reads. The raw
    // reading is no longer an input of this effect.
    socket.recentReadings,
    socket.confirmationByMember,
    socket.verdictByMember,
  ]);

  const getAnnouncementText = useCallback(() => {
    // Cycle 309: compose through the server-authoritative utterance
    // pipeline — the context resolves AT SPEAK TIME (this runs at the
    // cadence tick, never from a captured snapshot), the spoken zone is
    // the reading's server-stamped `classification` (never zoneOf + local
    // thresholds), and the veracity gate applies. `staleMode: "silent"`
    // keeps the cycle-235.3 rule: the unprompted cadence never reads out
    // a stale value (the explicit kiosk voice query speaks the stale
    // variant instead — it lives in useVoiceDispatch).
    // Cycle 435: the reading handed to the utterance is the SELECTOR's —
    // resolved at speak time, sampling its own clock — and only when it
    // is current. An unprovable reading (an expired confirmation, a server
    // `stale` verdict) is handed as `null`, which `staleMode: "silent"`
    // turns into silence, exactly as a stale-by-age reading always was.
    // Cycle 425's rule in one line: nothing speaks a glucose it did not
    // just receive.
    const memberId = activeMember.active?.member_id ?? null;
    const view = selectCurrentGlucoseFor(socket, memberId);
    const ctx = resolveUtteranceContext({
      reading: view.status === "current" ? view.reading : null,
      memberId,
      memberName,
      lastAlert: socket.lastAlert,
    });
    const r = composeGlucoseUtterance(ctx, {
      surface: "auto_announce",
      staleMode: "silent",
    });
    return r.text ?? "";
  }, [socket, memberName, activeMember.active?.member_id]);

  useAutoAnnounce({
    enabled: settingsCtx.settings?.alerts_enabled !== false,
    intervalMinutes: settingsCtx.settings?.tts_announce_interval_minutes ?? 0,
    speak: tts.speak,
    getText: getAnnouncementText,
    // Suppress auto-announce while ANY alarming episode is active —
    // not just urgent. Matches the cycle-20.x scope (low + urgent_*).
    urgentActive: episode.phase !== "idle",
    // Cycle 330: voice quiet hours — an ADDITIONAL suppression reason for
    // this cadence only. It cannot reach the alarm path.
    quietHours: {
      enabled: settingsCtx.settings?.tts_quiet_hours_enabled === true,
      start: settingsCtx.settings?.tts_quiet_hours_start ?? "22:00",
      end: settingsCtx.settings?.tts_quiet_hours_end ?? "07:00",
    },
    // Cycle 309: a due tick suppressed because the app just came to the
    // foreground (the suspended-timer catch-up fire) — telemetry only.
    onSuppressedResume: () =>
      reportVeracityEvent("suppress_resume", "auto_announce"),
    // Cycle 330: a due tick suppressed by quiet hours — telemetry only.
    onSuppressedQuietHours: () =>
      reportVeracityEvent("suppress_quiet_hours", "auto_announce"),
  });

  // Cycle 36: alarm-preview audio bridge. Each preview zone maps to
  // the same imperative method on `useAlarmSounds` that the real
  // alarm chain would call — looping for urgent levels + low, one-
  // shot for in_range / high. Stop loops on zone change / clear.
  // Belt-and-suspenders: cancel any active preview on shell unmount
  // so navigating away or signing out doesn't leave audio playing.
  // Cycle 351: `alarms` is a useMemo whose identity changes whenever any
  // of its many callback deps do. Keeping it in the dep array below meant
  // an unrelated re-render could re-run this effect while `preview.zone`
  // was still set and REPLAY the cue. The bridge must fire on a genuine
  // preview START only — `zone` + `startedAt` are that identity.
  const alarmsRef = useRef(alarms);
  alarmsRef.current = alarms;
  // Cycle 351 (SAFETY): is a REAL alarm episode currently driving audio?
  // Every preview-side teardown below is gated on this being false, so no
  // preview bookkeeping can ever pause a live alarm loop.
  const episodeAudioLiveRef = useRef(false);
  episodeAudioLiveRef.current = episode.audioOn === true;

  const preview = useAlarmPreview();

  // ─── Cycle 451.1: sounding alarm audio off /now returns to /now ───
  //
  // THE INVARIANT: at least one alarm control is on screen and hit-testable
  // whenever alarm audio is sounding (cycle 81). Cycle 451 restored the app
  // chrome on the treating screen, which is what made a caregiver able to
  // LEAVE `/now` during a window — and then reported, rather than fixed, that
  // `useAlarmSounds` is mounted once in this shell and so resumes the loop on
  // any route, while `AlarmSilenceGuard` and all three alarm banners mount
  // only inside `NowScreen`. This closes that with the smallest change that
  // can close it: put the app where the controls already are.
  //
  // ⚠ ONE PREDICATE. `alarms.audibleAlarmLevel` is the bridge's OWN output
  // from `audibleAlarmLevel(...)` — the same call that decides what to sound,
  // this same render. Nothing here reads `episode.phase`, `silenced`,
  // `treating`, `quietWindow` or `standDown`: a snoozed, stopped, quiet-window
  // or declared-treating episode is already not audible, so those exclusions
  // come free and cannot drift from the audio. That is cycle 451's lesson
  // (`treatingSurfaceMounted`) applied one cycle on.
  //
  // ⚠ REPLACE, NOT PUSH — Back must not bounce the caregiver into a loop.
  // Deliberately not edge-triggered: the deps carry the pathname, so leaving
  // `/now` while it is still sounding comes straight back, and once the audio
  // stops (Snooze, Stop where allowed, a server recovery, a resume handled)
  // the rule goes quiet and navigation is free.
  //
  // ⚠ WHAT THIS IS NOT. It writes no alarm, custody, snooze or recovery
  // state, adds no surface and changes none: `/now` already renders the
  // urgent takeover, the collapsed strip with Snooze, the custody takeover
  // and clock mode's takeover exactly as it would have had the user never
  // left. The cost is that an open modal, sheet or on-screen keyboard closes
  // with the navigation and an unsaved edit on that screen is lost — accepted
  // deliberately: a caregiver who cannot reach Snooze is the worse outcome.
  // ─── Cycle 453.2: an alarm that starts sounding closes the kiosk keyboard ─
  // Operator decision 4: on the kiosk, when an alarm starts sounding while a
  // text field has focus, blur the field (typed text stays) so the keyboard
  // closes and the alarm banner is fully visible at the moment it matters.
  // "Sounding" = the low-side audio the bridge drives, or the no-data tone.
  // Only the RISING edge acts — a caregiver may refocus the field on purpose
  // while the alarm continues (the banners then dock under the header).
  const alarmSoundingForKeyboard =
    alarms.audibleAlarmLevel != null || alarms.noDataAlarmActive;
  const prevAlarmSoundingRef = useRef(alarmSoundingForKeyboard);
  useEffect(() => {
    const was = prevAlarmSoundingRef.current;
    prevAlarmSoundingRef.current = alarmSoundingForKeyboard;
    if (variant !== "kiosk") return;
    if (!alarmStartedSounding(was, alarmSoundingForKeyboard)) return;
    if (typeof document !== "undefined") blurFocusedTextEntry(document);
  }, [alarmSoundingForKeyboard, variant]);

  // ─── Cycle 455: alarms beat the camera ──────────────────────────────────
  // On the kiosk, while ANY alarm is sounding (highs included; a Stopped high
  // is silenced and no longer counts), the photo buttons are disabled, and on
  // the rising edge the page tells the APK (window.sugalarmNative) so it
  // closes an open native camera or photo dialog and completes the chooser
  // with no file. The APK also refuses any chooser for 3 s after the message:
  // the backstop for a tap and an alarm racing. Silent no-op on an older APK.
  // This READS alarm state and writes none.
  const alarmSoundingForPhotos = alarmSoundingForPhotosFn({
    kiosk: variant === "kiosk",
    episodeAudioOn: episode.audioOn,
    lowSideAudible: alarms.audibleAlarmLevel != null,
    noDataAlarm: alarms.noDataAlarmActive,
  });
  const prevAlarmSoundingForPhotosRef = useRef(false);
  // A LAYOUT effect: the gate is published before the browser paints, so a
  // photo button never renders enabled for a frame while an alarm sounds
  // (astra implementation review, MEDIUM 4).
  useLayoutEffect(() => {
    const was = prevAlarmSoundingForPhotosRef.current;
    prevAlarmSoundingForPhotosRef.current = alarmSoundingForPhotos;
    setPhotoBlockedByAlarm(alarmSoundingForPhotos);
    if (alarmStartedForCamera(was, alarmSoundingForPhotos)) sendAlarmStarted();
  }, [alarmSoundingForPhotos]);

  // ─── Cycle 456: the "Who's treating?" picker closes when audio starts ──
  // Same three inputs as the photo gate, on EVERY surface (a phone's picker
  // closes too). A layout effect, so the picker is gone in the frame the
  // audio starts. READS alarm state and writes none.
  const alarmSoundingForPickerNow =
    episode.audioOn || alarms.audibleAlarmLevel != null || alarms.noDataAlarmActive;
  useLayoutEffect(() => {
    setAlarmSoundingForPicker(alarmSoundingForPickerNow);
  }, [alarmSoundingForPickerNow]);

  const { alarmSounding } = useAlarmRouteReturn({
    audibleLevel: alarms.audibleAlarmLevel,
    kioskUrgentRepeat: alarms.kioskUrgentRepeat,
    urgentCueSeq: alarms.urgentCueSeq,
    previewActive: preview.zone != null,
  });

  // ─── Cycle 452: the alarm controls follow the caregiver ────────────────
  // 451.1 moved the APP to the controls; this moves the controls to the app,
  // for the two places the pin does not reach: the pairing family (exempt on
  // purpose) and a phone after its one-shot urgent-low navigation. One
  // decision (`lib/alarmControl.ts`), built from values the audio bridge and
  // the treating screen already share — never a second list.
  //
  // ⚠ THE PIN IS UNCHANGED. This is additive: on `/now` the full surfaces
  // are on screen and this renders nothing.
  const alarmControlGlucose = selectCurrentGlucoseFor(
    socket,
    activeMember.fullMember?.id ?? socket.activeMemberId,
  ).displayValue;
  const goToAlarm = useCallback(() => {
    nav("/now", { replace: true });
  }, [nav]);
  const shellAlarmControl = compactAlarmControlVisible({
    episode,
    pathname: location.pathname,
  }) ? (
    <CompactAlarmControl
      episode={episode}
      current={alarmControlGlucose}
      units={units}
      // Cycle 452.2: Now's banner, where Now's banner is on each surface —
      // the frame positions the slot (bottom of the kiosk stage; under the
      // header on phones and web).
      placement="banner"
      // A handshake broken mid-flow can strand a device unpaired: on the
      // pairing family the control acts in place and never navigates.
      canNavigate={!isPairingPath(location.pathname)}
      onGoToAlarm={goToAlarm}
    />
  ) : null;

  // ─── Cycle 452: Log as an overlay on `/now` ─────────────────────────────
  // While sustained audio sounds the Log affordances open Log OVER Now
  // instead of navigating (the pin would bounce a navigation straight back).
  // See `hooks/useLogOverlay.ts`: one same-URL history entry, so Android Back
  // closes it and never navigates; no URL state; nothing persisted.
  const {
    open: logOverlayOpen,
    search: logOverlaySearch,
    openLogOverlay,
    closeLogOverlay,
  } = useLogOverlay();
  const logOverlayNode =
    logOverlayOpen && logOverlaySearch != null ? (
      <LogOverlay
        variant={variant}
        search={logOverlaySearch}
        alarmSounding={alarmSounding}
        // Docked for as long as a control is owed; on `/now` "go to alarm"
        // means reveal Now, i.e. close the overlay.
        dock={
          lowSideControlOwed(episode) != null ? (
            <CompactAlarmControl
              episode={episode}
              current={alarmControlGlucose}
              units={units}
              placement="docked"
              canNavigate
              onGoToAlarm={closeLogOverlay}
            />
          ) : null
        }
        onClose={closeLogOverlay}
      />
    ) : null;

  useEffect(() => {
    if (preview.zone == null) return;
    const alarms = alarmsRef.current;
    switch (preview.zone) {
      case "urgent_low":
        // Cycle 374: urgent low no longer loops on a real fire, so the
        // preview plays it once — same reasoning cycle 232 applied to
        // urgent_high. (The kiosk's repeating cue re-fires on a 30 s
        // escalating gap, well past the ~5 s preview window, so a single
        // play is a faithful audition there too.)
        alarms.playUrgentLowOnce();
        break;
      case "low":
        alarms.startLowLoop();
        break;
      case "in_range":
        alarms.playNormal();
        break;
      case "high":
        alarms.playHigh();
        break;
      case "urgent_high":
        // Cycle 232: urgent_high is no longer a continuous loop (it shares
        // the hourly high cadence). The preview plays a single high.wav so
        // it reflects the real, calmed urgent-high sound, not a loop.
        alarms.playHigh();
        break;
    }
    return () => {
      // Cycle 351 (SAFETY): NEVER stop audio while a real alarm episode is
      // driving it. There is exactly one `useAlarmSounds` instance, so the
      // preview and the real episode bridge share the SAME <audio>
      // elements — a blanket `stopAllLoops()` here pauses `lowRef` /
      // `urgentLowRef`, and the episode bridge does NOT re-arm (it
      // early-returns while `targetLevel` is unchanged), so a live
      // urgent-low loop would stay dead until the member recovers or
      // escalates. A false alarm is bad; a missed real low can kill.
      if (!episodeAudioLiveRef.current) alarmsRef.current.stopAllLoops();
    };
  }, [preview.zone, preview.startedAt]);

  useEffect(() => {
    return () => {
      cancelPreview();
    };
  }, []);

  // Cycle 351 (SAFETY, defense in depth): drop any in-flight preview the
  // moment the app is backgrounded. `getPreviewState()` already enforces
  // the wall-clock deadline so a suspended webview timer can no longer
  // strand a preview — this additionally guarantees a preview never
  // OUTLIVES the foreground session that started it, so there is nothing
  // for the audio bridge to replay on resume at all. Stopping-only: this
  // can never start audio, and it cannot reach a real alarm (the
  // server-driven cue paths in useAlarmSounds are untouched).
  // Gated on `episodeAudioLiveRef`: cancelling a preview re-renders and runs
  // the bridge cleanup, so doing it mid-episode is exactly the path that
  // could pause a live loop (see the cleanup note above). While a real alarm
  // is sounding there is nothing to protect anyway — the elements are the
  // real alarm's, and the episode bridge owns them.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onHide = () => {
      if (
        document.visibilityState === "hidden" &&
        !episodeAudioLiveRef.current
      ) {
        cancelPreview();
      }
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, []);

  // Cycle 76 / 216 / 236: speak a fired reminder aloud on EVERY foregrounded
  // surface (kiosk, web, iOS + Android shell) — not the kiosk only.
  // Driven by the overlay APPEARING (activeReminder going non-null). Never
  // speak over a looping
  // alarm (mirror useAutoAnnounce's `episode.phase !== "idle"` suppression),
  // and never while the app is backgrounded — a hidden mobile app relies on
  // the native push. The spoken voice is the active member's `tts_voice`
  // (set globally via tts.setVoice).
  // Cycle 309: wall-clock of the last hidden→visible transition — the
  // foreground-grace gate for reminder speech below (the auto-announce
  // cadence tracks its own copy inside useAutoAnnounce).
  const lastVisibleAtRef = useRef<number>(Date.now());
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => {
      if (document.visibilityState === "visible") {
        lastVisibleAtRef.current = Date.now();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Cycle 361: the ONE predicate deciding whether the reminder takeover is on
  // screen — used by both the announce effect below and the render gate.
  //
  // It yields on exactly TWO conditions, and to nothing else:
  //
  //  (1) The alarm can actually SOUND (`episode.audioOn`, i.e.
  //      `phase === "active" && !silenced`). Withholding here honours the
  //      cycle-81 floor — "loop could sound ⇒ Snooze/Stop on screen" —
  //      EXACTLY, since the reminder is a z-9000 fullscreen takeover that
  //      would otherwise bury those controls.
  //
  //  (2) A LOW-SIDE episode is live at all, muted or not (`episodeLowSide`,
  //      the shared cycle-311.1 predicate). This is the "Lows kill — Highs do
  //      not" policy: a hypo outranks a medication reminder, so the reminder
  //      never covers the glucose number or the snooze countdown during one.
  //      A hypo can therefore delay a reminder past its 10-minute window — a
  //      deliberate, policy-directed trade, and the server-side push fan-out
  //      still went out at fire time regardless.
  //
  // A live but muted HIGH-side episode does NOT withhold the reminder. That is
  // the whole point of cycle 361: on the measured account a high-side episode
  // sat muted-but-live for a median 5.3 h across 18 of 21 days, which is what
  // suppressed the 10:00 reminder and then re-showed it mid-afternoon.
  //
  // Because this is a render-time predicate it self-corrects the instant an
  // alarm re-arms; and because the auto-dismiss deadline lives in
  // useReminderOverlay (NOT here), a reminder withheld by either condition
  // still ages out on schedule instead of resurfacing hours later.
  // (Cycle 473.1: the two conditions are the named `alarmOutranks`
  // (lib/alarmOutranksOverlay.ts) above, shared with the kiosk Wi-Fi gate; same truth table.)
  const reminderVisible = activeReminder != null && !alarmOutranks;

  // Announce (chime → speech) ONCE per fired reminder, at the moment its
  // takeover first becomes visible.
  //
  // Cycle 361 — why this is keyed on first-visible rather than on
  // `activeReminder` alone: the pre-361 effect bailed on
  // `episode.phase !== "idle"` with deps `[activeReminder]`, so a reminder
  // arriving during ANY live episode was never chimed or spoken and was never
  // retried when the alarm cleared — on the measured account that silently
  // killed the kiosk chime + voice on 18 of 21 days. Gating on `audioOn`
  // instead is also truer to the original cycle-216 intent ("never speak over
  // a LOOPING alarm"): a snoozed/Stopped alarm is not looping.
  const announcedReminderIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeReminder == null) {
      // Cleared (Close, cross-device dismiss, or the 10-min deadline) — re-arm
      // so tomorrow's occurrence of the SAME reminder announces again.
      announcedReminderIdRef.current = null;
      return;
    }
    if (!reminderVisible) return;
    if (announcedReminderIdRef.current === activeReminder.id) return;
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    )
      return;
    // Cycle 309 (operator rule: no unprompted speech at app open): on a
    // NON-kiosk surface, a reminder overlay that appears within the
    // foreground grace window is a resume / catch-up artifact — a
    // `reminder_fired` frame delivered while the app was backgrounded and
    // processed on resume. Show the overlay SILENTLY (it still demands the
    // tap); skip only the chime + speech. The kiosk never backgrounds, so its
    // fire-time chime+speech is unchanged.
    if (
      variant !== "kiosk" &&
      Date.now() - lastVisibleAtRef.current < FOREGROUND_GRACE_MS
    ) {
      reportVeracityEvent("suppress_resume", "reminder_speech");
      return;
    }
    // Cycle 243.5: the requested sequence — play the `sugalarm_reminder`
    // chime first, THEN speak the message once the chime ends (the chime is
    // ~3.2s; `playReminderChime` calls back on `ended`, with a fallback so
    // the TTS still speaks if the chime is autoplay-blocked). A fresh fire
    // sets `activeReminder`, firing the full chime+speak cycle; Close clears it.
    const message = activeReminder.message;
    // Mark BEFORE the async chime callback so a re-render can never
    // double-announce the same fire.
    announcedReminderIdRef.current = activeReminder.id;
    alarms.playReminderChime(() => {
      // Cycle 330: voice quiet hours gate ONLY the spoken utterance. The
      // reminder still fully arrives — the <ReminderOverlay> takeover is
      // already on screen (it is mounted independently of this effect) and
      // the server-side push fan-out is untouched. The household just isn't
      // read to aloud at 3am. Evaluated HERE, at speak time, rather than
      // when the chime started.
      //
      // This is not an alarm path: it cannot silence an alarm sound, an
      // alert, the urgent overlay, or the manual Speak Status button.
      const qhOn = settingsCtx.settings?.tts_quiet_hours_enabled === true;
      if (
        qhOn &&
        isWithinQuietHours(
          new Date(),
          settingsCtx.settings?.tts_quiet_hours_start ?? "22:00",
          settingsCtx.settings?.tts_quiet_hours_end ?? "07:00",
        )
      ) {
        reportVeracityEvent("suppress_quiet_hours", "reminder_speech");
        return;
      }
      tts.speakReminder(message);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReminder, reminderVisible]);

  // Cycle 100 — wake hygiene + reconcile. All three effects no-op unless
  // the kiosk voice agent is reachable (web/mobile never enter them).
  //
  // (1) Earcon on wake. The agent→SPA SSE stream surfaces each detected
  // wake as a new `voice.lastWakeTs`; play the short earcon. The banner
  // pulse is rendered by <WakeBanner> inside KioskFrame off the same
  // value. Cycle 101: defer to alarms — suppress the earcon (and, in the
  // dispatcher, all spoken answers) while an alarm episode is active, so
  // we never chirp/talk over the alarm. Read the phase via a ref so a
  // phase change can't re-fire the earcon (only a new wake does).
  const alarmActiveRef = useRef(false);
  alarmActiveRef.current = episode.phase !== "idle";
  const lastWakeTs = voice.lastWakeTs;
  useEffect(() => {
    if (lastWakeTs == null) return;
    if (variant !== "kiosk") return;
    if (alarmActiveRef.current) return; // defer to alarms
    playWakeEarcon();
  }, [lastWakeTs, variant]);

  // (2) Suppress wake detection while the kiosk is itself speaking, so the
  // cycle-16 auto-announce read-out (and manual Speak Status / reminder
  // TTS — all flow through the same `tts.speaking`) can't self-trigger the
  // wake word. Pause for the agent's ceiling on speech start; resume the
  // instant it ends. The agent's pause ceiling is the missed-resume
  // backstop, so the mic can never wedge off.
  const voiceAvailable = voice.available;
  const setWakePaused = voice.setWakePaused;
  const ttsSpeaking = tts.speaking;
  useEffect(() => {
    if (variant !== "kiosk" || !voiceAvailable) return;
    if (ttsSpeaking) {
      void setWakePaused(true, 30_000);
    } else {
      void setWakePaused(false);
    }
  }, [ttsSpeaking, variant, voiceAvailable, setWakePaused]);

  // (3) Reconcile server↔agent. The device's wake word is server-
  // authoritative (Settings → CGM & devices, assignable from a phone);
  // the agent's local cache is the instant-on copy. On a reachable kiosk,
  // read THIS device's server-side `wake_word` (resolve "which device am
  // I" from the device-token session) and push it to the agent if it
  // differs and is actually available locally. If the saved word isn't on
  // this Pi, leave the agent on its current word — Settings surfaces the
  // mismatch warning. Real-time cross-device propagation is out of scope;
  // this runs on mount / agent-ready, so a phone change lands on reload.
  const voiceActiveKey = voice.activeKey;
  const voiceOptions = voice.options;
  const voiceSetWakeWord = voice.setWakeWord;
  useEffect(() => {
    if (variant !== "kiosk" || !voiceAvailable) return;
    const deviceId = getDeviceId();
    if (!deviceId) return;
    let cancelled = false;
    void (async () => {
      const r = await devicesApi.list();
      if (cancelled || !r.ok) return;
      const me = r.data.devices.find((d) => d.id === deviceId);
      const saved = me?.wake_word ?? null;
      if (!saved) return; // unset → agent keeps its cache/default
      if (saved === voiceActiveKey) return; // already in sync
      if (!voiceOptions.some((o) => o.key === saved)) return; // not on this Pi
      await voiceSetWakeWord(saved);
    })();
    return () => {
      cancelled = true;
    };
  }, [variant, voiceAvailable, voiceActiveKey, voiceOptions, voiceSetWakeWord]);

  // Cycle 101 — voice conversation dispatcher (kiosk-only). Consumes
  // `voice.lastIntent` from the agent's Rhino layer, answers the five
  // spoken queries from in-memory state (offline-capable), and runs the
  // confirm-back + optimistic-insert + offline-queue write path for
  // `logInsulin`. `enabled` gates it to a kiosk with a reachable agent, so
  // web/mobile do nothing. `urgentActive` defers all voice while an alarm
  // episode is active.
  const voiceDispatch = useVoiceDispatch({
    enabled: variant === "kiosk" && voice.available,
    voice,
    tts,
    socket,
    activeMemberId: activeMember.active?.member_id ?? null,
    memberName,
    urgentActive: episode.phase !== "idle",
  });

  // Cycle 383 — hands-free voice commands on ANDROID (the tablet kiosk APK
  // + the phone app). One implementation, two surfaces. It is a permanent
  // no-op on the iOS shell, on iPad, and on the web: the native
  // `VoiceCapture` plugin is not on the bridge there, so nothing runs and
  // no microphone is ever requested.
  //
  // This cycle is DEV-GATED — inert unless `localStorage.sugalarm_voice_dev`
  // is "1" on the device — so no user sees anything until the "Hey Sugalarm"
  // wake word lands next cycle. With the flag unset there is no trigger
  // listener, no plugin call, and no fetch.
  //
  // SAFETY: `alarmLive` is passed READ-ONLY. Voice cannot start during a
  // live alarm and is cancelled the instant one fires mid-session. Nothing
  // in this layer can silence, snooze, stop or acknowledge an alarm — the
  // grammar cannot express it.
  const voiceCommands = useVoiceCommands({
    variant,
    tts,
    socket,
    activeMemberId: activeMember.active?.member_id ?? null,
    memberName,
    alarmLive: episode.isLive,
  });

  // Cycle 170: web status-strip tile select → switch the viewed (active)
  // member via the EXISTING active-member path (the same setActive +
  // refresh HouseholdScreen uses). Scoped to the accessible set the API
  // enforces; no client-side authorization bypass.
  const onSelectMember = useCallback(
    async (memberId: string) => {
      if (memberId === (activeMember.active?.member_id ?? null)) return;
      const r = await membersApi.setActive(memberId);
      if (r.ok) await activeMember.refresh();
    },
    [activeMember],
  );

  const predictionPill = useMemo(() => {
    const p = socket.lastPrediction;
    if (!p || p.outcome != null) return null;
    const horizonEnd =
      new Date(p.predicted_at).getTime() + p.horizon_minutes * 60_000;
    if (horizonEnd <= now) return null;
    const pretty = p.predicted_level
      .replace("predicted_", "")
      .replace("_", " ");
    return `Predicted ${pretty} in ~${p.horizon_minutes} min`;
  }, [socket.lastPrediction, now]);

  return (
    <>
    {/* Cycle 316: first-run alarm-permissions onboarding tour (iOS +
        Android mobile shells only — the component self-gates and renders
        null everywhere else). It ALSO renders null while an alarm episode
        is live: the alarm path wins unconditionally; the tour resumes
        where it was once the episode clears. */}
    {/* Cycle 464: owns every subscription refetch (mount, resume, 10 min). */}
    <SubscriptionProvider signedIn={auth.status === "signedIn"} accountId={auth.user?.id ?? null} />
    <AlarmOnboardingTour
      episodeLive={episode.isLive}
      noDataAlarmActive={alarms.noDataAlarmActive}
    />
    {/* Cycle 328: the gentle "add a photo" avatar nudge (soft-dismiss +
        don't-remind-me-again). Self-gates on every variant (renders null
        unless the active member has no avatar, setup has settled, it's a
        camera-capable surface, and no alarm/clock is up). It sits BELOW the
        alarm overlays in z-order and renders null while an episode is live,
        so an alarm always wins. */}
    <AvatarNudge
      variant={variant}
      activeMember={activeMember.active}
      episodeLive={episode.isLive}
      clockActive={clockActive}
      onAvatarUpdated={() => void activeMember.refresh()}
    />
    {/* Cycle 365: activation prompt — "alarms can't reach this phone".
        Driven by the SERVER's verdict (so it can't disagree with the admin
        activation roster), self-gating: renders null when there's no gap,
        while an episode is live, and on surfaces with no native alarm rail
        to activate. Sits below every alarm surface in z-order. */}
    <ActivationPrompt
      verdict={activation.verdict}
      episodeLive={episode.isLive}
      variant={variant}
      onAlertId={handleAlertIdNav}
      onRecheck={activation.recheck}
    />
    {/* Cycle 236: fullscreen reminder takeover — the sole reminder display
        surface (z-index 9000, above everything).
        Cycle 361: gated on `reminderVisible` (see its definition above), which
        yields ONLY to an alarm that can actually sound — so the alarm's
        Snooze/Stop controls are never covered (cycle-81 floor) while a merely
        muted (snoozed / Stopped) episode no longer suppresses the reminder for
        hours. The overlay auto-dismisses 10 minutes after the fire arrived
        (useReminderOverlay), so it can never resurface later on. */}
    {reminderVisible && activeReminder != null ? (
      <ReminderOverlay
        reminder={activeReminder}
        isKiosk={variant === "kiosk"}
        // Cycle 453.2 (operator decision 3, option a): the kiosk no-data
        // banner draws above this takeover; keep Close clear of its band.
        reserveAlarmBand={variant === "kiosk" && alarms.noDataAlarmActive}
        // Cycle 362: name the time the reminder was FOR, not the current
        // clock. Null on a `/test` fire, where "now" is the honest answer.
        scheduledLabel={
          activeFire != null
            ? formatReminderTime(
                activeFire.time_of_day,
                settingsCtx.settings?.display_24h === true,
              )
            : null
        }
        onClose={() => {
          // Cycle 359: single Close. Dismiss optimistically here, then tell
          // the server so the overlay clears on every one of the owner's
          // devices in lockstep.
          // Cycle 362: when the fire carried a durable occurrence, Close the
          // OCCURRENCE — that stamps `dismissed_at`, so the acknowledgement
          // survives a reload/reboot and a device that was offline for the
          // broadcast converges on its next snapshot. Fall back to the
          // reminder-scoped route for a `/test` fire (no durable row).
          const fire = activeFire;
          const id = activeReminder.id;
          reminderOverlay.dismiss();
          if (fire != null) {
            void remindersApi.dismissFire(fire.id);
          } else {
            void remindersApi.dismiss(id);
          }
        }}
      />
    ) : null}
    {/* Cycle 362: outstanding-reminder chip — the trace that makes the
        cycle-361 10-minute auto-dismiss safe. Shows occurrences the server
        still considers un-acknowledged, EXCLUDING the one the takeover is
        currently showing (so the two never duplicate). Withheld entirely
        while an alarm episode is live and in bedside clock mode; it is a
        slim z-30 strip that can never cover an alarm control. */}
    {!episode.isLive && !clockActive ? (
      <MissedReminderBanner
        fires={socket.outstandingReminders.filter(
          (f) => !(reminderVisible && activeFire != null && f.id === activeFire.id),
        )}
        h24={settingsCtx.settings?.display_24h === true}
        isKiosk={variant === "kiosk"}
        onDismiss={(fire) => {
          void remindersApi.dismissFire(fire.id);
        }}
      />
    ) : null}
    <KioskFrame
      variant={variant}
      user={auth.user}
      socket={socket}
      tts={tts}
      // Cycle 222: episode-level urgent (stays true through the recovery
      // dwell) — the frame-level SpeakingBanner uses it to stay safety-quiet
      // (never paint over an active urgent alarm).
      urgentActive={
        episode.isLive &&
        (episode.episodeLevel === "urgent_low" ||
          episode.episodeLevel === "urgent_high")
      }
      // Cycle 394: a live episode means the kiosk Now screen may be showing
      // the bottom alarm strip, so lift the SpeakingBanner clear of it. A
      // deliberate superset of the strip's mount gate — see the prop's note.
      alarmStripActive={episode.isLive && !episode.bannerSuppressedInRange}
      clockFaceActive={clockFaceActive}
      now={now}
      units={units}
      memberName={memberName}
      memberAvatarId={memberAvatarId}
      // ⚠ CYCLE 451 — THE CHROME ALLOWLIST, AND NOTHING ELSE. Every term
      // is one NAMED SURFACE from `lib/appChrome.ts`; the chrome is on
      // unless one of them is up. No alarm state is readable from here,
      // by construction: a treating window, a custody prompt, a quiet
      // window or a pending check-in cannot reach this expression, which
      // is exactly the defect cycle 451 closed (see `appChrome.ts` and
      // `treatingChrome.test.ts`). Adding a surface means editing the
      // allowlist and its pinned copy in the guard.
      showChrome={showAppChrome({
        login: location.pathname === "/login",
        // Cycle 145: the bedside clock FACE owns the viewport while it is
        // the surface on screen. Cycle 402 made `clockFaceActive` the one
        // expression for that fact, so the frame's no-data gate
        // (`showChrome || clockFaceActive`) is provably the complement of
        // exactly this term; cycle 451 made the expression true only when
        // the face really is the surface (see its derivation above).
        clockFace: clockFaceActive,
        // Cycle 220: the Android-tablet pairing takeover (the cycle-175
        // relink /pair, which renders inside this frame) fills the full
        // 1024×640 stage — suppress the EdgeRail/header/footer chrome so
        // none shows behind it. Gated on the same `nativeEntry` predicate
        // (kiosk variant + Android tablet); the Pi/iPad relink keeps its
        // chrome, and the cold-boot /pair already renders outside this frame.
        // Cycle 452.1: `/pair-device` renders the same takeover, so the flag
        // names it too. Every other route is untouched. (Cycle 453.1: the
        // `compactFloats` twin of this expression is gone — its last reader
        // was the no-data banner's takeover term, retired with the 402 rule.)
        // `isPairingDisplayPath` also takes a trailing slash (React Router
        // mounts `/pair-device/` too — astra, cycle 452.1); the literal
        // `/pair` term stays for `alarmStripLift.test.ts`'s locator.
        pairingTakeover:
          (location.pathname === "/pair" || isPairingDisplayPath(location.pathname)) &&
          variant === "kiosk" &&
          isAndroidTabletClassRuntime(),
      })}
      predictionPill={predictionPill}
      voiceWakeTs={voice.lastWakeTs}
      // Cycle 383: a hands-free voice command session is capturing or
      // thinking — drives the SAME WakeBanner (kiosk + mobile portrait).
      voiceListening={voiceCommands.listening}
      members={activeMember.members}
      activeMemberId={activeMember.active?.member_id ?? null}
      onSelectMember={onSelectMember}
      noDataAlarmActive={alarms.noDataAlarmActive}
      onSilenceNoData={alarms.silenceNoData}
      // Cycle 453.2 (operator decision 3, option a): the reminder takeover
      // paints outside the stage; while it is up the no-data banner draws in
      // the stage-aligned layer above it.
      alarmTopLayer={reminderVisible && activeReminder != null}
      alarmControl={shellAlarmControl}
    >
      {/* Cycle 468: the OWNER's lockout. On a kiosk surface a full-stage
          surface with the sentence and no control (painted over the stale
          last screen; the routes below stay mounted but covered). On a
          phone or the web the lockout REPLACES every route a locked person
          does not need — Settings (Subscription, Restore, delete account),
          sign-in and recovery, pairing and admin stay reachable. Never
          while an episode is live (a lock never lands mid-alarm; this is the
          belt for a stale client-side episode).
          ⚠ The KIOSK lockout (safety review V2): it paints over the Now
          screen's urgent-low takeover, whose Snooze sits below the alarm
          layer, so it is withheld whenever an episode or the No Data alarm
          is live for the ACTIVE member — and shown only while the active
          member is itself LOCKED (the kiosk's own household), never while
          the kiosk is showing a followed member whose owner has service. */}
      {ownerLockedOut &&
      (variant === "kiosk" || isKioskSurface()) &&
      !episode.isLive &&
      !alarms.noDataAlarmActive &&
      (activeMemberLocked || activeMember.active == null) ? (
        <KioskLockout />
      ) : null}
      {ownerLockedOut &&
      !(variant === "kiosk" || isKioskSurface()) &&
      !episode.isLive &&
      lockoutCoversPath(location.pathname) ? (
        <LockoutScreen accountId={auth.user?.id ?? null} onSignOut={auth.signOut} />
      ) : (
        <>
          {/* Cycle 468: the owner's grace banner — in flow above the route,
              phone and web only; renders nothing during an alarm or a No
              Data alarm, so it can never cover an alarm control. */}
          {variant !== "kiosk" && !isKioskSurface() ? (
            <GraceBanner
              subscription={subscriptionStore.data}
              alarmLive={episode.isLive}
              noDataAlarmActive={alarms.noDataAlarmActive}
              platform={variant === "mobile" ? (capacitorPlatform() === "ios" ? "ios" : capacitorPlatform() === "android" ? "android" : "web") : "web"}
            />
          ) : null}
      <Routes>
        {/* Cycle 453.1: inside the kiosk frame the screen fits the space the
            frame gives it (the No Data banner shares the stage with it); the
            signed-out /login above is bare and keeps full viewport height. */}
        <Route
          path="/login"
          element={
            <LoginScreen
              auth={auth}
              fitFrame={variant === "kiosk"}
              alarmLive={episode.isLive || alarms.noDataAlarmActive}
            />
          }
        />
        {/* Cycle 453.2 (operator decision 2): a signed-in session opening a
            reset link gets the page INSIDE the frame, so the alarm layer
            covers it. Signed out, the bare branch above renders instead. */}
        <Route path="/reset-password" element={<ResetPasswordScreen fitFrame />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen fitFrame />} />
        {/* Cycle 170: web command-station Dashboard (web-only landing) +
            Reports placeholder. Both guard non-web variants → /now. */}
        <Route
          path="/dashboard"
          element={
            <DashboardScreen
              variant={variant}
              socket={socket}
              units={units}
              now={now}
              memberName={memberName}
              memberAvatarId={memberAvatarId}
              activeMemberId={activeMember.active?.member_id ?? null}
            />
          }
        />
        <Route
          path="/reports"
          element={<ReportsScreen variant={variant} memberName={memberName} />}
        />
        <Route
          path="/now"
          element={
            <>
            <NowScreen
              socket={socket}
              now={now}
              variant={variant}
              activeMember={activeMember.fullMember}
              units={units}
              episode={episode}
              // Cycle 402: the clock face must RESERVE the band the
              // no-data strip occupies, or the strip lands on the clock
              // digits (cycle 394's rule). Both the frame's mount gate and
              // this reservation read the SAME `noDataAlarmActive`, which
              // is the single source in `useAlarmSounds` — so they cannot
              // disagree about whether a strip is there.
              noDataAlarmActive={alarms.noDataAlarmActive}
              // Cycle 468: a followed member whose owner is locked out (or
              // the owner's own, by the owner's own subscription read).
              serviceLocked={activeMemberLocked}
              // Cycle 451.1: sustained alarm audio is sounding. Feeds the
              // Log affordances' explanation ONLY — no alarm control, no
              // sound and no gate depends on it. Same value the route rule
              // pins on, so the screen and the rule cannot disagree.
              alarmSounding={alarmSounding}
              // Cycle 452: while that audio sounds, Log opens over Now.
              onOpenLogOverlay={openLogOverlay}
              // Cycle 407: the check-in speaks through the ONE app-level
              // TTS owner (the instance reminders + auto-announce use), so
              // its element is already primed by the tap that opened the
              // window. Quiet hours are deliberately NOT consulted here.
              speak={tts.speak}
              // Cycle 404: who is holding this screen. Feeds the "I can
              // help after all" affordance ONLY — no alarm control, no
              // sound and no gate depends on it. `variant !== "kiosk"` is
              // the term that protects the tablet (a device session
              // authenticates as the household owner, so an id resolves
              // there too); this is just the identity half.
              userId={userId}
              // Cycle 383: both voice paths share ONE persistent queue
              // (lib/treatmentQueue). Prefer the live owner's count.
              pendingSyncCount={
                voiceCommands.pendingCount ?? voiceDispatch.pendingCount
              }
              clockMode={clockMode}
            />
            {/* Cycle 452: Log over Now while the alarm sounds — a sibling of
                the screen, never a route. */}
            {logOverlayNode}
            </>
          }
        />
        <Route path="/pair-approve" element={<PairingApprovePanel />} />
        {/* Cycle 331: onboarding "pair this device to a household" entry,
            reached from the CGM-provider screen's pair-device card. Always
            force-display (regardless of session type — a JWT-holding
            visitor still sees a code, never the "you're signed in" nudge);
            back returns to the provider step, not /now (which may not
            exist yet without a CGM linked — see the needsCgm exemption
            above). */}
        <Route
          path="/pair-device"
          element={
            <KioskPairingScreen forceDisplay fitFrame={variant === "kiosk"} backTo="/login" backLabel="Back" />
          }
        />
        <Route
          path="/history"
          element={<HistoryScreen variant={variant} />}
        />
        <Route path="/log" element={<LogScreen variant={variant} />} />
        <Route
          path="/log/recent"
          element={<RecentEntriesScreen variant={variant} />}
        />
        <Route
          path="/log/photo"
          element={<PhotoMealScreen variant={variant} />}
        />
        <Route path="/alerts" element={<AlertsScreen />} />
        <Route
          path="/settings"
          element={
            <SettingsScreen
              auth={auth}
              activeMember={activeMember}
              tts={tts}
              variant={variant}
              episode={episode}
              episodeRecoveredAt={socket.lastAlert?.recovered_at ?? null}
              socket={socket}
              now={now}
              clockMode={clockMode}
              alarms={alarms}
            />
          }
        />
        <Route
          path="/household"
          element={
            <HouseholdScreen
              user={auth.user}
              socket={socket}
              units={units}
              variant={variant}
              now={now}
              activeMemberId={activeMember.active?.member_id ?? null}
              refreshActive={activeMember.refresh}
            />
          }
        />
        <Route
          path="/pair"
          element={
            // Cycle 175: a paired kiosk is a device-token (signed-in)
            // session — render the code-DISPLAY screen in force-display
            // mode (it offers a fresh code + a back control) so "Pair this
            // screen" works on an already-paired kiosk.
            // Cycle 221: a kiosk-VARIANT device on a JWT session (e.g. an
            // Android tablet set up via account login, not pairing) also
            // OFFERS a code — render the same force-display takeover so the
            // Settings "Pair this screen" button reaches it without the
            // tablet being a device session yet. Offering a code is
            // harmless (the request endpoint is unauthenticated); approving
            // (entering a code) stays JWT-only, on the phone. A web/mobile
            // JWT user still gets nudged to the approve (entry) screen.
            auth.isDeviceSession || variant === "kiosk" ? (
              <KioskPairingScreen forceDisplay fitFrame={variant === "kiosk"} />
            ) : (
              <button
                onClick={() => nav("/pair-approve")}
                style={{
                  margin: 32,
                  appearance: "none",
                  border: "0.5px solid var(--line-strong)",
                  background: "var(--bg-elev)",
                  color: "var(--ink)",
                  borderRadius: 12,
                  padding: "12px 16px",
                }}
              >
                You're signed in — tap to approve a kiosk instead →
              </button>
            )
          }
        />
        {/* Cycle 170: web lands on the Dashboard; kiosk/mobile keep /now. */}
        <Route
          path="*"
          element={<Navigate to={variant === "web" ? "/dashboard" : "/now"} replace />}
        />
      </Routes>
        </>
      )}
    </KioskFrame>
    <PreviewOverlay
      episode={episode}
      tts={tts}
      activeMember={activeMember.fullMember}
      user={auth.user}
      units={units}
      variant={variant}
    />
    {/* Cycle 88: active member's CGM credentials were definitively
        rejected — prompt an in-place update. Gated on the WS/snapshot
        flag, so a successful re-connect on any device clears it and the
        modal closes everywhere. */}
    {socket.cgmAuthFailed ? (
      <CgmAuthModal
        variant={variant}
        memberName={activeMember.fullMember?.display_name ?? null}
        // Cycle 453.2 (safety review, astra re-review): step aside only while
        // an alarm surface needs the screen (audio, the lead window, the
        // treating/quiet screen, an owed low-side control). NOT for the whole live episode: a Stopped high row stays live
        // on a dark feed, and hiding the one sign that the CGM login was
        // refused would leave a dark feed with nothing on screen.
        // Only on /now: off it, the alarm controls (the off-Now banner, the
        // No Data banner) sit ABOVE the modal, so hiding it gains nothing.
        stepAside={isNowPath(location.pathname) && alarmSurfaceNeedsScreen(episode)}
      />
    ) : null}
    </>
  );
}
